import React from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  Clock,
  Users,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Calendar,
  Download,
  Phone,
  Timer,
  Zap,
  Target,
  BarChart3,
  MessageCircle
} from 'lucide-react';

const QuizResultsDemo = () => {
  // Sample calculated results for demonstration
  const results = {
    advisorHoursSaved: 248,
    productivityHoursSaved: 1440,
    totalTimeSaved: 1688,
    workingDaysEquivalent: 211,
    advisorCostSavings: 11160,
    productivityValue: 31680,
    totalMonthlyValue: 42840,
    vioqbaseMonthlyFee: 1500,
    netMonthlySavings: 41340,
    annualSavings: 496080,
    automationRate: 75,
    speedImprovement: 360, // 6 hours * 60 minutes
    currentMonthlyCost: 20160,
    totalMonthlyQueries: 4000,
    automatedQueries: 3000
  };

  const formData = {
    firstName: 'John',
    companyName: 'TechCorp Ltd',
    employees: '500',
    waitTime: '6'
  };

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  const handleDownloadReport = () => {
    alert('PDF report download will be implemented with backend!');
  };

  const handleContactSales = () => {
    alert('Contact sales functionality will be implemented with backend!');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Results Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
              🎉 Great News, <span className="text-[#ff7a1f]">{formData.firstName}</span>!
            </h1>
            <h2 className="text-2xl lg:text-3xl font-semibold text-blue-100">
              VioQbase can save {formData.companyName}:
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                <CardContent className="p-6">
                  <DollarSign className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">£{results.netMonthlySavings.toLocaleString()}</div>
                  <div className="text-blue-200">per month</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                <CardContent className="p-6">
                  <Clock className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">{results.totalTimeSaved}</div>
                  <div className="text-blue-200">hours per month</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                <CardContent className="p-6">
                  <TrendingUp className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">{results.automationRate}%</div>
                  <div className="text-blue-200">productivity boost</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Results Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto space-y-16">
            
            {/* Time Savings Section */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                🕒 Time VioQbase Will Save {formData.companyName}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardHeader className="text-center pb-4">
                  <Timer className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-[#112d56]">Monthly Advisor Hours Saved</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-4xl font-bold text-[#112d56] mb-2">{results.advisorHoursSaved}</div>
                  <div className="text-gray-600">hours per month</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardHeader className="text-center pb-4">
                  <Users className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-[#112d56]">Employee Productivity Hours</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-4xl font-bold text-[#112d56] mb-2">{results.productivityHoursSaved}</div>
                  <div className="text-gray-600">hours per month</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardHeader className="text-center pb-4">
                  <Calendar className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-[#112d56]">Annual Time Savings</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-4xl font-bold text-[#112d56] mb-2">{results.totalTimeSaved * 12}</div>
                  <div className="text-gray-600">hours per year</div>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center bg-[#ff7a1f]/10 rounded-2xl p-8 mb-16">
              <p className="text-xl font-semibold text-[#112d56]">
                That's equivalent to <span className="text-[#ff7a1f] font-bold">{results.workingDaysEquivalent} full working days</span> returned to your team every month
              </p>
            </div>

            {/* Financial Savings Section */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                💸 Money VioQbase Will Save {formData.companyName}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.advisorCostSavings.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Monthly Advisor Savings</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.productivityValue.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Productivity Value Savings</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-[#ff7a1f]/10">
                <CardContent className="p-6 text-center">
                  <Target className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.netMonthlySavings.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Total Monthly Savings</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-green-50">
                <CardContent className="p-6 text-center">
                  <BarChart3 className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-green-600 mb-2">£{results.annualSavings.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Annual Impact</div>
                </CardContent>
              </Card>
            </div>

            {/* Investment Comparison */}
            <Card className="border-0 shadow-xl mb-16">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-[#112d56] text-center">Investment Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div className="p-4">
                    <div className="text-lg font-semibold text-gray-700 mb-2">VioQbase Monthly Cost</div>
                    <div className="text-2xl font-bold text-[#ff7a1f]">£{results.vioqbaseMonthlyFee.toLocaleString()}</div>
                  </div>
                  <div className="p-4">
                    <div className="text-lg font-semibold text-gray-700 mb-2">Your Current Monthly Cost</div>
                    <div className="text-2xl font-bold text-red-600">£{results.currentMonthlyCost.toLocaleString()}</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-lg font-semibold text-gray-700 mb-2">Net Monthly Savings</div>
                    <div className="text-3xl font-bold text-green-600">£{results.netMonthlySavings.toLocaleString()}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workplace Productivity Section */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                📈 How VioQbase Increases {formData.companyName}'s Productivity
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Zap className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">{results.automationRate}%</div>
                  <div className="text-sm text-gray-600">Employee Questions Automated With VioQbase</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Timer className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">From {formData.waitTime}h to 0</div>
                  <div className="text-sm text-gray-600">Wait Time Reduced</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">{results.advisorHoursSaved}h</div>
                  <div className="text-sm text-gray-600">Advisor Hours Freed Up</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <MessageCircle className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                  <div className="text-2xl font-bold text-[#112d56] mb-2">{Math.round(results.speedImprovement / 60)}x</div>
                  <div className="text-sm text-gray-600">Faster Responses</div>
                </CardContent>
              </Card>
            </div>

            {/* Before/After Comparison */}
            <Card className="border-0 shadow-xl bg-gradient-to-r from-gray-50 to-white mb-16">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-[#112d56] text-center">Before vs After VioQbase</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-red-50 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-red-700 mb-4">Current State</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• {formData.employees} employees asking {results.totalMonthlyQueries.toLocaleString()} questions monthly</li>
                      <li>• Advisors spending {Math.round((results.totalMonthlyQueries * 15) / 60)} hours on routine queries</li>
                      <li>• Employees waiting {formData.waitTime} hours for answers</li>
                      <li>• Monthly cost: £{results.currentMonthlyCost.toLocaleString()}</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-green-700 mb-4">With VioQbase</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• {results.automationRate}% of questions automated instantly</li>
                      <li>• Advisors focus on complex issues only</li>
                      <li>• Zero wait time for routine questions</li>
                      <li>• Monthly cost: £{results.vioqbaseMonthlyFee.toLocaleString()} (including VioQbase)</li>
                      <li className="font-bold text-green-600">• Net benefit: £{results.netMonthlySavings.toLocaleString()} saved + {results.totalTimeSaved} hours returned</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card className="border-0 shadow-2xl bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white overflow-hidden">
              <CardContent className="p-12 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                <div className="relative z-10">
                  <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                    Ready to unlock these savings for <span className="text-[#ff7a1f]">{formData.companyName}</span>?
                  </h3>
                  <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                    Are you ready to start saving time and money with VioQbase?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={handleDemo}
                      size="lg"
                      className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                    >
                      <Calendar className="mr-2 h-5 w-5" />
                      Contact Us
                    </Button>
                    <Button
                      onClick={handleDownloadReport}
                      size="lg"
                      variant="outline"
                      className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Report
                    </Button>
                    <Button
                      onClick={handleContactSales}
                      size="lg"
                      variant="outline"
                      className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Contact Sales
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuizResultsDemo;